// ----------------------------------------------------------------------------
// Product Form Web Component: iamCartConfirmation <iam-cart-confirmation>
// Extends: iamModal
// ----------------------------------------------------------------------------
// Handle the display of the "cart confirmation modal"
// - When a product is being added to the cart, modify the AJAX request to include
//   a request to the section rendering API to fetch the `cart-confirmation__item`
//   and `cart-confirmation__summary` sections. This content will be used to
//   populate and then open the modal if the product is successfully added.
// ----------------------------------------------------------------------------
// Event Listeners:
// - 'productForm:submitting:data'   → Modifies an add-to-cart request to include
//                                     a request for the 'cart-confirmation__item'
//                                     and 'cart-confirmation__summary' sections
// - 'productForm:submitted:success' → Triggered when the product form is successfully
//                                     submitted. It updates the modal contents
//                                     and opens it.
// ----------------------------------------------------------------------------
// Gotchas:
// - On add-to-cart failure, the product form should show an inline error
//   (i.e. it will not open the cart confirmation modal)
// - The Liquid code should include the `cart-confirmation` section to create
//   a hidden shell for the modal that will be dynamically populated
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Helpers
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import iamModal from 'snippets/_containers/modal/modal';
import { Loadable } from 'scripts/classes/iamota-component-helpers';

// Styles
import './_cart-confirmation.scss';

// -------------------------------------
// Web Component
// -------------------------------------

class iamCartConfirmation extends iamModal {
  constructor() {
    super();
    this.loadable = new Loadable(this);
  }
  connectedCallback() {
    this.cartConfirmationItem = this.querySelector('.cart-confirmation__item');
    this.cartConfirmationContent = this.querySelector('.cart-confirmation__content');

    // Handle Continue Shopping button click
    this.handleContinueShoppingClick = this.handleContinueShoppingClick.bind(this);
    this.addEventListener('click', this.handleContinueShoppingClick);
    iamotaEventHandler.subscribe('productForm:submitting:data', this.onProductFormSubmittingData.bind(this), {
      source: "cart-confirmation.js"
    });
    iamotaEventHandler.subscribe('productForm:submitted:success', this.onProductFormSubmitted.bind(this), {
      source: "cart-confirmation.js"
    });
    super.connectedCallback();
  }
  handleContinueShoppingClick(event) {
    var continueShoppingButton = event.target.closest('.button__continue-shopping');
    if (continueShoppingButton) {
      event.preventDefault();
      this.closeModal();
    }
  }
  onProductFormSubmittingData(request) {
    var _request$sections;
    this.setLoading(true);
    var sections = new Set((_request$sections = request.sections) === null || _request$sections === void 0 ? void 0 : _request$sections.split(',')); // Existing sections requested
    sections.add('cart-confirmation__item');
    sections.add('cart-confirmation__summary');
    request.sections = Array.from(sections).join(',');
    return request;
  }
  onProductFormSubmitted(_ref) {
    var _iamProductForm$getCu, _addedItem$quantity;
    var cart = _ref.cart,
      iamProductForm = _ref.iamProductForm,
      submitter = _ref.submitter,
      isCheckoutButton = _ref.isCheckoutButton;
    console.log("Submitted", cart);
    var cartItems = Array.isArray(cart === null || cart === void 0 ? void 0 : cart.items) ? cart.items : cart !== null && cart !== void 0 && cart.key ? [cart] : [];
    var currentVariantId = iamProductForm === null || iamProductForm === void 0 || (_iamProductForm$getCu = iamProductForm.getCurrentVariant) === null || _iamProductForm$getCu === void 0 || (_iamProductForm$getCu = _iamProductForm$getCu.call(iamProductForm)) === null || _iamProductForm$getCu === void 0 ? void 0 : _iamProductForm$getCu.id;
    var addedItem = cartItems.find(item => currentVariantId && String(item === null || item === void 0 ? void 0 : item.variant_id) === String(currentVariantId)) || cartItems[0];
    var itemKey = addedItem === null || addedItem === void 0 ? void 0 : addedItem.key;
    var quantity = (_addedItem$quantity = addedItem === null || addedItem === void 0 ? void 0 : addedItem.quantity) !== null && _addedItem$quantity !== void 0 ? _addedItem$quantity : iamProductForm.getCurrentQuantity();
    var sections = (cart === null || cart === void 0 ? void 0 : cart.sections) || (addedItem === null || addedItem === void 0 ? void 0 : addedItem.sections);
    if (itemKey && quantity && sections && sections['cart-confirmation__item'] && sections['cart-confirmation__summary']) {
      var itemHTML = sections['cart-confirmation__item'];
      var summaryHTML = sections['cart-confirmation__summary'];
      this.openCartConfirmation(itemKey, quantity, itemHTML, summaryHTML, submitter, isCheckoutButton);
    } else {
      console.warn('Cannot open Cart Confirmation Modal (missing data in the cart response)', itemKey, quantity, sections, cart);
    }
    this.setLoading(false);
  }
  openCartConfirmation(itemKey, quantity, itemHTML, summaryHTML, triggerElement, isCheckoutButton) {
    var _window$CSS;
    var parsedHTML = new DOMParser().parseFromString(itemHTML, 'text/html');
    var escapedItemKey = (_window$CSS = window.CSS) !== null && _window$CSS !== void 0 && _window$CSS.escape ? window.CSS.escape(itemKey) : itemKey;
    var itemNode = parsedHTML.querySelector("[id=\"cart-confirmation__item--".concat(escapedItemKey, "\"]")) || parsedHTML.querySelector('.cart-confirmation__item');
    if (!itemNode) {
      console.warn('Cannot render Cart Confirmation Modal item (missing rendered item HTML)', {
        itemKey
      });
      return;
    }

    // Update the modal content
    this.cartConfirmationItem.innerHTML = itemNode.innerHTML;
    var addedQtyElement = this.cartConfirmationItem.querySelector('.added-quantity__value');
    if (addedQtyElement) addedQtyElement.textContent = quantity;
    this.cartConfirmationContent.innerHTML = summaryHTML;

    // Open the modal if not triggered by the checkout button
    if (!isCheckoutButton) this.openModal(triggerElement);
  }

  /**
   * Expose Loading state methods
   */
  setLoading(isLoading) {
    this.loadable.setLoading(isLoading);
  }
  isLoading() {
    return this.loadable.isLoading();
  }
}
customElements.define('iam-cart-confirmation', iamCartConfirmation);